import { Button, Divider, Drawer, Select, Space } from 'antd';
import { getTranslatedString } from 'helpers/i18n';
import React, { useContext } from 'react';
import { ApplicationUIContext, Form, Table, useDrawerState } from 'ui-library';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { StyledSpace } from '../common/StyledComponents';
import { renderAmountPerMonth, startingMonthOptions } from './common/common';
import { requiredRule } from '../common/Rules';

const ScheduledEventsTableRowActions = ({
  onUpdate,
  onDelete,
  isCalculationTypePercentage,
  record,
}) => {
  const [, editScheduledEventDrawer] = useDrawerState(false);
  const [editScheduledEventForm] = Form.useForm();
  const { isMobile } = useContext(ApplicationUIContext);

  const editScheduledEventText = `${getTranslatedString('edit')} ${getTranslatedString(
    'landlordPortal.incentives.scheduledEvents'
  )}`;

  const handleScheduledEventsDrawerClose = () => {
    editScheduledEventForm.resetFields();
    editScheduledEventDrawer.close();
  };

  return (
    <>
      <Space size="middle">
        <Button type="link" icon={<EditOutlined />} onClick={editScheduledEventDrawer.open} />
        <Button type="link" icon={<DeleteOutlined />} onClick={onDelete} />
      </Space>

      <Drawer
        title={editScheduledEventText}
        visible={editScheduledEventDrawer.isVisible}
        closable
        destroyOnClose
        onClose={editScheduledEventDrawer.close}
      >
        <Form
          layout="vertical"
          size={isMobile ? 'large' : 'middle'}
          form={editScheduledEventForm}
          name="edit-scheduled-event"
          scrollToFirstError
          initialValues={{
            ...record,
          }}
          onFinish={(values) => {
            onUpdate(values);
            handleScheduledEventsDrawerClose();
          }}
        >
          <StyledSpace direction="vertical" size="middle">
            <Form.Item
              label={getTranslatedString('landlordPortal.incentives.monthOfLease')}
              name="month_of_lease"
              rules={[requiredRule]}
            >
              <Select options={startingMonthOptions} />
            </Form.Item>

            {renderAmountPerMonth(isCalculationTypePercentage)}
          </StyledSpace>

          <Divider />

          <Space direction="horizontal">
            <Button type="primary" htmlType="submit">
              {getTranslatedString('landlordPortal.incentives.add')}
            </Button>
            <Button type="secondary" onClick={handleScheduledEventsDrawerClose}>
              {getTranslatedString('landlordPortal.incentives.cancel')}
            </Button>
          </Space>
        </Form>
      </Drawer>
    </>
  );
};

const ScheduledEventsTable = ({ events, isCalculationTypePercentage, operations }) => {
  const { isMobile } = useContext(ApplicationUIContext);
  const scheduledEventsForm = Form.useFormInstance();

  const columns = [
    {
      title: getTranslatedString('landlordPortal.incentives.monthOfLease'),
      dataIndex: 'month_of_lease',
      key: 'month_of_lease',
      render(_, row) {
        return (
          <Form.Item shouldUpdate>
            {({ getFieldValue }) => {
              return getFieldValue(['scheduled_events', row.name, 'month_of_lease']);
            }}
          </Form.Item>
        );
      },
    },
    {
      title: getTranslatedString('landlordPortal.incentives.amount'),
      dataIndex: 'amount_per_month',
      key: 'amount_per_month',
      render(_, row) {
        return (
          <Form.Item shouldUpdate>
            {({ getFieldValue }) => {
              const prefix = !isCalculationTypePercentage ? '$' : '';
              const suffix = isCalculationTypePercentage ? '%' : '';
              const amount_per_month = getFieldValue([
                'scheduled_events',
                row.name,
                'amount_per_month',
              ]);
              return `${prefix}${amount_per_month}${suffix}`;
            }}
          </Form.Item>
        );
      },
    },
    {
      key: 'actions',
      render: (_, record, index) => (
        <ScheduledEventsTableRowActions
          onUpdate={(updatedRecord) => {
            operations.remove(record.name);
            operations.add(updatedRecord);
          }}
          onDelete={() => operations.remove(record.name)}
          record={scheduledEventsForm.getFieldValue('scheduled_events')[index]}
          isCalculationTypePercentage={isCalculationTypePercentage}
        />
      ),
    },
  ];

  return (
    <Table
      dataSource={events}
      columns={columns}
      hideActionAndSearch
      size={isMobile ? 'middle' : 'small'}
      scroll={{ x: isMobile }}
    />
  );
};

export default ScheduledEventsTable;
